import { useLazyQuery, useQuery, useReactiveVar } from '@apollo/client';
import { useLocation } from '@reach/router';
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeElementLocale, StripeElementsOptions } from '@stripe/stripe-js';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import React, { FC, useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FORM_PAGES } from '../../../components/Form/types';
import { ProssecingPayment } from '../../../components/FormInfo/BookingOrder/styled';
import FormInfo from '../../../components/FormInfo/Summary/FormInfo';
import {
  FORM_INFO_COMPONENT_FIELD_TYPES,
  FORM_INFO_COMPONENT_SLOT_TYPES,
  FORM_INFO_COMPONENT_TYPES,
  FORM_INFO_FIELD_TYPES,
  FORM_INFO_SLOT_TYPES,
  FORM_INFO_TYPES
} from '../../../components/FormInfo/types';
import { FULL_DAYS, THREE_DAYS, formatOrdinals, getTimestampDescription, isProductPostpaid, toReadableDate } from '../../../components/FormRadio/helpers';
import { Button } from '../../../components/Shared/FormContainer/styled';
import { CenteredLoadingSpinner } from '../../../components/Shared/LoadingSpinner/LoadingSpinner';
import { ErrorMessage } from '../../../components/Shared/Shared';
import useMemoizedFullDates from '../../../hooks/useMemoizedFullDates';
import { vars } from '../../../reactive';
import { setFormPage } from '../../../reactive/actions';
import { getBranchCurrencySymbol, getISOByCountryName, replaceVariables, sentenceCase } from '../../../utils';
import config from '../../../utils/config';
import { GetCardAuthentication, GetMultiOrderPreOrderSummary, GetPreOrderSummary, GetRecurringAppointmentsPreOrderSummary, GetUserProfile } from '../../../utils/queries';
import getStripe from '../../../utils/stripe';

const isDev = config.ENV === 'development';

const FormSummary: FC<{
  loadingOrderSummary?: boolean;
  loadingRecurrence?: boolean;
  loadingMultiOrderSummary?: boolean;
  multiOrderSummaries?: Record<string, any>[];
  orderSummary?: Record<string, any>;
  submitData?: () => void;
  recurringSummaries?: Record<string, any>[];
  recurringSummaryTimestamps?: Date[];
  shouldGetCardAuth?: boolean;
}> = ({ loadingOrderSummary, loadingRecurrence, loadingMultiOrderSummary, multiOrderSummaries, orderSummary, submitData, recurringSummaries, recurringSummaryTimestamps, shouldGetCardAuth }) => {
  const { t, i18n } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const { handleSubmit, register, control, formState, watch } = useForm();
  const selectedProduct = vars.selectedProduct()!;
  const selectedBranch = vars.currentBranch();
  const selectedOrderGroupSlots = vars.selectedOrderGroupSlots();
  const selectedSlots = selectedOrderGroupSlots[0].slots;
  const isMultiOrder = selectedOrderGroupSlots.length > 1;
  const selectedPets = vars.selectedPets();
  const recurrenceType = vars.recurrenceType();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const productType = selectedProduct.type!;
  const bookingType = selectedProduct.booking_type!;
  const subsciptionProduct = productType === 'subscription';
  const isRecurring = recurrenceType === 'recurring';
  const slotType = isRecurring ? FORM_INFO_SLOT_TYPES.RECURRENCE : subsciptionProduct ? FORM_INFO_SLOT_TYPES.SUBSCRIPTION : (bookingType as FORM_INFO_SLOT_TYPES);
  const slotComponentType = FORM_INFO_COMPONENT_SLOT_TYPES[slotType];
  const multiDayProduct = bookingType === FORM_INFO_SLOT_TYPES.MULTI_DAY;
  const selectedCustomizations = vars.selectedCustomizations() || [];
  const total = Number(orderSummary?.total);
  const totalString = orderSummary?.total !== undefined ? `${getBranchCurrencySymbol()}${total.toFixed(2)}` : 'Calculating...';
  const isPostPaid = isProductPostpaid(selectedProduct!);
  const isFree = total === 0;
  const selectedDefaultPaymentMethod = useWatch({
    name: FORM_INFO_COMPONENT_TYPES.SUMMARY.registerAs,
    control,
    defaultValue: true
  });

  const groupsFullDates = useMemoizedFullDates({
    selectedOrderGroupSlots,
    selectedProduct
  });

  const fullDates = groupsFullDates[0] || [];

  const { loading: getUserProfileLoading, data: { getProfile: userProfile = {} as Queries.CollarAPI_AppUser } = {} } = useQuery<{
    getProfile: Queries.CollarAPI_AppUser;
  }>(GetUserProfile, {
    fetchPolicy: 'cache-and-network'
  });

  const { postcode, name, addresses, payment } = userProfile;

  const loadingSummary = loadingOrderSummary || loadingRecurrence || loadingMultiOrderSummary;

  const isAlreadyBooked = isMultiOrder ? [...(multiOrderSummaries || [])]?.some(summary => summary?.isAlreadyBooked) : orderSummary?.isAlreadyBooked;

  const isAlreadySubscribed = orderSummary?.isAlreadySubscribed;

  const onSubmit = async (data: Record<string, string>) => {
    setErrorMessage('');
    if (isAlreadyBooked) {
      return;
    }

    submitData?.();

    if (data.SELECTED_DEFAULT_PAYMENT_METHOD || !shouldGetCardAuth) {
      return setFormPage(FORM_PAGES.BOOKING_ORDER);
    }

    setLoading(true);

    const { error } = await stripe!.confirmSetup({
      elements: elements!,
      confirmParams: {
        return_url: location.href,
        payment_method_data: {
          billing_details: {
            name: name || '',
            address: {
              line1: addresses?.[0]?.line1 || '',
              line2: addresses?.[0]?.line2 || '',
              city: addresses?.[0]?.city || '',
              country: getISOByCountryName(addresses?.[0]?.country) || '',
              postal_code: postcode || ''
            }
          }
        }
      }
    });
    setLoading(false);
    if (error) {
      setErrorMessage(error.message!);
    }
  };

  const isAlreadyAppointed = isAlreadyBooked || isAlreadySubscribed;

  const isMultiSlot = bookingType === FORM_INFO_SLOT_TYPES.MULTI_SLOT;

  const showVoucherInput = !isRecurring && !isMultiOrder;

  const nextButtonSiblings = !!isAlreadyAppointed && <ErrorMessage>{t(slotComponentType.buttonErrorTitle)}</ErrorMessage>;

  const appUserCredit = userProfile?.AppUserCredit || { amount: 0 };

  const finalDiscount = +orderSummary?.discount;

  const isDiscounted = !!finalDiscount || (appUserCredit.amount > 50 && !subsciptionProduct);
  const hasCustomizations = !!selectedCustomizations?.length;

  const priceSummary = orderSummary?.productsWithTotals?.[0]?.productTotals?.priceSummary || '';
  const finalPriceWithDeliveryAndCredits = `${getBranchCurrencySymbol()}${(Number(orderSummary?.finalPrices?.finalPriceWithDeliveryAndCredits) || 0).toFixed(2)}`;
  const custimazationsDescription =
    hasCustomizations &&
    selectedCustomizations?.map(customization =>
      Object.keys(customization.fields)
        .map(customKey => {
          const price = Number(customization.fields[customKey].price);
          const priceString = !!price ? `+ (${getBranchCurrencySymbol()}${price})` : '';
          return `${customKey}: ${customization.fields[customKey]?.title} ${priceString}`;
        })
        .join('\n')
    );

  const getFullDatesDateDescription = fullDates => {
    return fullDates.map(
      (fullDate, i) =>
        getTimestampDescription(fullDate.timestamp, {
          noTime: multiDayProduct || isRecurring
        }) + (fullDates[i + 1] ? '\n' : '')
    );
  };

  const getFullDateDateUntilDescription = fullDates => {
    return fullDates.map(
      (fullDate, i) =>
        getTimestampDescription(fullDate?.timestamp_until, {
          noTime: true
        }) + (fullDates[i + 1] ? '\n' : '')
    );
  };

  const dateDescription = !subsciptionProduct && getFullDatesDateDescription(fullDates);

  const dateUntilDescription = multiDayProduct && getFullDateDateUntilDescription(fullDates);

  const multiDateDescription = groupsFullDates.map(group => getFullDatesDateDescription(group).join('')).join('\n');

  const multiDateUntilDescription = multiDayProduct && groupsFullDates.map(group => getFullDateDateUntilDescription(group).join('')).join('\n');

  const selectedDays = selectedSlots?.[0]?.days || [];
  const selectedDaysIndeces = selectedDays.map(day => THREE_DAYS.indexOf(day));
  const selectedDaysDescription = selectedDaysIndeces.map(index => t(`daysFull.${FULL_DAYS[index]}`)).join(', ');
  const slotTime = selectedSlots?.[0]?.time;
  const slotRepeatition = selectedSlots?.[0]?.repetition;
  const repeatition = `${slotTime}, ${t('repeatedFor')} ${slotRepeatition?.repeat} ${t('weeks')}`;
  const totalTimestaps = !loadingRecurrence
    ? `${recurringSummaryTimestamps?.length || 0} ${recurringSummaryTimestamps?.length === 1 ? t('slot') : t('slots')} ${t('summary.recurrence.availability')}`
    : t('calculating');
  const recurringDateDescription = `${t('starting')}, ${dateDescription}`;
  const dateTitle = {
    type: FORM_INFO_FIELD_TYPES.DATE,
    replacedTitle: replaceVariables(FORM_INFO_COMPONENT_FIELD_TYPES.DATE.title, { slot: t(slotComponentType.dateTitle) })
  };

  const appliedVouchersRules = orderSummary?.appliedVouchers?.map(voucher => voucher?.Discount?.rules?.reduce((acc, rule) => `${acc} ${rule?.description}`, '')).join(', ');

  const multiAppliedVouchersRules = [...(multiOrderSummaries || recurringSummaries || [])]
    .map(summary => {
      const appliedVouchersRules = summary?.appliedVouchers?.map(voucher => voucher?.Discount?.rules?.reduce((acc, rule) => `${acc} ${rule?.description}`, '')).join(', ');
      return appliedVouchersRules;
    })
    .filter(Boolean);

  const items = [
    {
      type: FORM_INFO_FIELD_TYPES.PRODUCT,
      description: `${selectedProduct!.name}`,
      replacedTitle: replaceVariables(FORM_INFO_COMPONENT_FIELD_TYPES.PRODUCT.title, { productType: sentenceCase(selectedProduct!.type) })
    },
    hasCustomizations && {
      type: FORM_INFO_FIELD_TYPES.VARIATIONS,
      description: custimazationsDescription
    },
    {
      type: FORM_INFO_FIELD_TYPES.PETS,
      description: selectedPets.map(pet => pet.name).join(', ')
    },
    !subsciptionProduct &&
      !isRecurring &&
      !isMultiOrder && {
        ...dateTitle,
        description: dateDescription
      },
    // isMultiOrder && {
    //   ...dateTitle,
    //   description: multiDateDescription
    // },
    isRecurring && {
      ...dateTitle,
      descriptionsArray: [{ value: selectedDaysDescription }, { value: repeatition }, { value: recurringDateDescription }, { value: totalTimestaps, style: 'bold' }],
      descriptionColumn: true
    },
    multiDayProduct &&
      !isMultiOrder && {
        type: FORM_INFO_FIELD_TYPES.DATE_UNTIL,
        description: dateUntilDescription
      },
    multiDayProduct &&
      isMultiOrder && {
        type: FORM_INFO_FIELD_TYPES.DATE_UNTIL,
        description: multiDateUntilDescription
      },
    !isMultiOrder &&
      !isRecurring && {
        type: FORM_INFO_FIELD_TYPES.PRICE,
        descriptionsArray: !isFree && [
          { value: `${t('total')}:` },
          { value: `${totalString}${subsciptionProduct ? '/Month' : ''}`, crossed: isDiscounted, style: 'bold' },
          { value: isDiscounted ? finalPriceWithDeliveryAndCredits : '' },
          { value: priceSummary }
        ],
        description: isFree && '-'
      },
    (isMultiOrder || isRecurring) && {
      type: FORM_INFO_FIELD_TYPES.MULTI_PRICE,
      descriptionsArray: [
        ...[...(multiOrderSummaries || recurringSummaries || [])].map((summary, index) => {
          const total = Number(summary?.total);
          const totalString = summary?.total !== undefined ? `${getBranchCurrencySymbol()}${total.toFixed(2)}` : t('calculating');
          const finalPriceWithDeliveryAndCredits = `${getBranchCurrencySymbol()}${(Number(summary?.finalPrices?.finalPriceWithDeliveryAndCredits) || 0).toFixed(2)}`;
          const isDiscounted = total !== Number(summary?.finalPrices?.finalPriceWithDeliveryAndCredits);
          const recurringTimestamp = recurringSummaryTimestamps?.[index] || groupsFullDates[index]?.[0]?.timestamp?.fullDate;
          const priceSummary = summary?.productsWithTotals?.[0]?.productTotals?.priceSummary || '';
          return [
            { value: recurringTimestamp ? `${toReadableDate(recurringTimestamp, { noTime: !isMultiOrder, withDay: true })}: ` : `${formatOrdinals(index + 1, i18n.language)} Booking: ` },
            { value: `${totalString} ${priceSummary}`, crossed: isDiscounted, style: 'bold' },
            { value: isDiscounted ? finalPriceWithDeliveryAndCredits : '' }
          ];
        })
      ],
      descriptionColumn: true
    },
    (isMultiOrder || isRecurring) &&
      !!multiAppliedVouchersRules.length && {
        type: FORM_INFO_FIELD_TYPES.APPLIED_DISCOUNT,
        descriptionsArray: multiAppliedVouchersRules.map(rules => {
          return [{ value: rules }];
        }),
        descriptionColumn: true
      },
    appliedVouchersRules && {
      type: FORM_INFO_FIELD_TYPES.APPLIED_DISCOUNT,
      description: appliedVouchersRules
    },
    Number(appUserCredit.amount) > 50 &&
      subsciptionProduct && {
        type: FORM_INFO_FIELD_TYPES.DISCOUNT_SUBSCRIPTION
      }
  ].filter(item => !!item) as {
    replacedTitle?: string;
    type: FORM_INFO_FIELD_TYPES;
    description?: string | string[];
    descriptionsArray?: { crossed?: boolean; value: string; style?: string }[];
    descriptionColumn?: boolean;
  }[];

  // const shouldDisplayPaymentDescription = isFree || isPostPaid;
  // const paymentDescriptionType = isPostPaid ? FORM_INFO_PAYMENT_DESCRIPTION.POSTPAID : FORM_INFO_PAYMENT_DESCRIPTION.NO_CHARGE;
  // const paymentDescription = shouldDisplayPaymentDescription ? FORM_INFO_COMPONENT_PAYMENT_DESCRIPTION[paymentDescriptionType] : '';
  // const replacedPaymentDescription = replaceVariables(paymentDescription, { branch: selectedBranch?.name });

  const paymentDescription = selectedProduct?.additional_info?.payment_description || '';

  return (
    <FormInfo
      type={FORM_INFO_TYPES.SUMMARY}
      formOptions={{ handleSubmit, register, control, formState, watch }}
      onSubmit={onSubmit}
      loading={!stripe || !elements || loading || getUserProfileLoading || loadingSummary}
      error={errorMessage}
      containerError={isAlreadyAppointed || (isMultiSlot && !groupsFullDates[0]?.length)}
      scrollable
      payment={payment}
      nextButtonSiblings={nextButtonSiblings}
      selectedDefaultPaymentMethod={selectedDefaultPaymentMethod}
      previousPage={{
        onPrevious: () => {
          let newPage: FORM_PAGES = slotComponentType.previousPage;
          if (vars.formsAnswers().length) {
            setFormPage(FORM_PAGES.BOOKING_FORMS, 'fade-out', { shouldNavigateToLastPage: true });
            return;
          }
          setFormPage(newPage);
        },
        animation: 'fade-out'
      }}
      items={items}
      bookingType={bookingType}
      nextButtonText={selectedProduct?.cta_text || (subsciptionProduct ? t('subscribe') : isFree || isPostPaid ? t('book') : t('summary.payAndBook'))}
      paymentDescription={paymentDescription}
      showVoucherInput={showVoucherInput}
      terms_and_conditions_url={selectedBranch?.terms_and_conditions_url}
      branchName={selectedBranch?.name}
      shouldGetCardAuth={shouldGetCardAuth}
      additionalInfo={selectedBranch?.additional_info}
    />
  );
};

function FormSummaryElements() {
  const { t, i18n } = useTranslation();
  const branch = useReactiveVar(vars.currentBranch);
  const { data: { getProfile: { addresses } = {} as Queries.CollarAPI_AppUser } = {} } = useQuery<{ getProfile: Queries.CollarAPI_AppUser }>(GetUserProfile);

  const [getCardAuthentication, { data: { getCardAuthentication: { clientSecret, status, ephemeralKey, customerId } = {} as Queries.CollarAPI_CustomerCardAuthentication } = {}, error: cardError }] =
    useLazyQuery<{
      getCardAuthentication: Queries.CollarAPI_CustomerCardAuthentication;
    }>(GetCardAuthentication, {
      fetchPolicy: 'no-cache'
    });

  const merchantCountryCode = getISOByCountryName(addresses?.[0]?.country ?? branch?.address?.country ?? 'United Kingdom');

  const options: StripeElementsOptions = {
    clientSecret: clientSecret,
    ...(merchantCountryCode !== 'US' && {
      allowedCardBrands: ['visa', 'mastercard', 'discover_global_network']
    }),
    appearance: {
      theme: 'flat'
    },
    loader: 'always',
    locale: i18n.language as StripeElementLocale

    // we need to ask stripe for beta to show payment methods like so:
    // customerOptions: {
    //   ephemeralKey,
    //   customer: customerId,
    // }
  };

  const stripe = getStripe();
  const selectedProduct = vars.selectedProduct()!;
  const selectedOrderGroupSlots = vars.selectedOrderGroupSlots();
  const selectedSlots = selectedOrderGroupSlots[0].slots;
  const isMultiOrder = selectedOrderGroupSlots.length > 1;
  const selectedPets = vars.selectedPets();
  const recurrenceType = vars.recurrenceType();
  const vouchers = useReactiveVar(vars.vouchers);
  const productType = selectedProduct.type!;
  const subsciptionProduct = productType === 'subscription';
  const isRecurring = recurrenceType === 'recurring';
  const selectedCustomizations = vars.selectedCustomizations() || [];
  const customizationsQuantity = 1;

  const groupsFullDates = useMemoizedFullDates({
    selectedOrderGroupSlots,
    selectedProduct
  });

  const fullDates = groupsFullDates[0] || [];

  const customizationsFields = Object.keys(selectedProduct.customizations).length
    ? selectedCustomizations.map(customization => ({
        ...customization,
        quantity: customizationsQuantity
      }))
    : null;

  const orderItems = [
    {
      id: selectedProduct?.id,
      quantity: 1,
      customizations: customizationsFields
    }
  ];
  const newOrderData = {
    items: orderItems,
    ...(!subsciptionProduct && {
      appointments: selectedPets
        .map(pet =>
          fullDates.map((date, index) => ({
            PetRecordId: pet.PetRecord!.id,
            timestamp: date!.timestamp!.fullDate,
            BusUserId: selectedSlots?.[index]?.BusUserId ? [selectedSlots[index].BusUserId] : undefined,
            timestamp_until: selectedProduct.booking_type === FORM_INFO_SLOT_TYPES.MULTI_DAY ? date!.timestamp_until!.fullDate : null
          }))
        )
        .flat()
    }),
    ...(subsciptionProduct && {
      subscriptions: selectedPets.map(pet => ({
        PetRecordId: pet!.PetRecord!.id
      }))
    }),
    vouchers: vouchers?.length ? vouchers : []
  };

  const recurringNewOrderData = {
    weeks_count: selectedSlots?.[0]?.repetition?.repeat,
    start_date: fullDates?.[0]?.timestamp?.fullDate,
    day_of_week: (selectedSlots?.[0]?.days || []).map(day => THREE_DAYS.indexOf(day)),
    PetRecordId: selectedPets?.map(pet => pet.PetRecord?.id),
    customizations: orderItems[0].customizations,
    ProductId: selectedProduct?.id
  };

  const multiOrderData = selectedOrderGroupSlots.map((orderGroup, index) => ({
    items: orderItems,
    appointments: selectedPets
      .map(pet =>
        groupsFullDates[index].map((date, index) => ({
          PetRecordId: pet.PetRecord!.id,
          BusUserId: orderGroup?.slots?.[index]?.BusUserId ? [orderGroup.slots[index].BusUserId] : undefined,
          timestamp: date!.timestamp!.fullDate,
          timestamp_until: orderGroup.product?.booking_type === FORM_INFO_SLOT_TYPES.MULTI_DAY ? date!.timestamp_until!.fullDate : null
        }))
      )
      .flat()
  }));

  const [getOrderSummary, { data: { getPreOrderSummary = {} } = {}, loading: loadingOrderSummary, called: calledSummary, error: errorSummary }] = useLazyQuery(GetPreOrderSummary, {
    variables: newOrderData,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false
  });

  const [getRecurringOrderSummary, { data: { getRecurringAppointmentsPreOrderSummary = {} } = {}, loading: loadingRecurrence, called: calledRecurrence, error: errorRecurring }] = useLazyQuery(
    GetRecurringAppointmentsPreOrderSummary,
    {
      variables: recurringNewOrderData,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false
    }
  );

  const [getMultiOrderSummary, { data: { getMultiOrderPreOrderSummary = {} } = {}, loading: loadingMultiOrderSummary, called: calledMultiOrderSummary, error: errorMultiOrder }] = useLazyQuery(
    GetMultiOrderPreOrderSummary,
    {
      variables: { getMultiOrderPreOrderSummaryInput: { getMultiOrderPreOrderSummary: multiOrderData } },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false
    }
  );

  const orderSummary = isMultiOrder ? getMultiOrderPreOrderSummary?.summariesTotal : isRecurring ? getRecurringAppointmentsPreOrderSummary?.summariesTotal : getPreOrderSummary || {};

  const getSummary = () => {
    if (isMultiOrder) {
      getMultiOrderSummary();
      return;
    }
    if (isRecurring) {
      getRecurringOrderSummary();
      return;
    }
    getOrderSummary();
  };

  const getAuthentication = () => {
    if (!paymentOptional && orderSummary?.total !== undefined) {
      getCardAuthentication();
    }
  };

  useEffect(() => {
    if (orderSummary?.total) {
      vars.orderSummary(orderSummary);
    }
  }, [orderSummary]);

  useEffect(() => {
    getSummary();
  }, [recurrenceType, isMultiOrder]);

  const total = Number(orderSummary?.total);
  const isPostPaid = isProductPostpaid(selectedProduct!);
  const isFree = total === 0;
  const paymentOptional = !!selectedProduct?.payment?.method_optional && (isFree || isPostPaid) && orderSummary?.total !== undefined;

  useEffect(() => {
    getAuthentication();
  }, [paymentOptional, orderSummary?.total]);

  const multiOrderSummaries = getMultiOrderPreOrderSummary?.summaries;
  const recurringSummaries = getRecurringAppointmentsPreOrderSummary?.summaries;

  const recurringSummaryTimestamps = getRecurringAppointmentsPreOrderSummary?.timestamps;

  const submitData = () => {
    vars.newOrders(isMultiOrder ? multiOrderData : isRecurring ? [recurringNewOrderData] : [newOrderData]);
  };

  const firstLoadingSummary = (loadingOrderSummary && !calledSummary) || (loadingRecurrence && !calledRecurrence) || (loadingMultiOrderSummary && !calledMultiOrderSummary);

  const error = errorSummary || errorRecurring || errorMultiOrder || cardError;

  const props = {
    loadingOrderSummary,
    loadingRecurrence,
    loadingMultiOrderSummary,
    multiOrderSummaries,
    orderSummary,
    submitData,
    recurringSummaries,
    recurringSummaryTimestamps,
    shouldGetCardAuth: !paymentOptional
  };

  const shouldShowSummary = !!clientSecret || paymentOptional;

  const retry = () => {
    getSummary();
    getAuthentication();
  };

  return (
    <>
      {error && (
        <ProssecingPayment>
          {t('summary.errorMessage')}{' '}
          {isDev && (
            <Button type="button" onClick={retry}>
              {t('summary.retry')}
            </Button>
          )}
        </ProssecingPayment>
      )}
      {(!shouldShowSummary || firstLoadingSummary) && !error && <CenteredLoadingSpinner />}

      {shouldShowSummary && !firstLoadingSummary && (
        <Elements stripe={stripe} options={options}>
          <FormSummary {...props} />
        </Elements>
      )}
    </>
  );
}

export default FormSummaryElements;
